import useClerkInfo from "../hooks/useClerkInfo";
import useDebouncedInput from "../hooks/useDebouncedInput";
import {
  useCreateHubspotContactMutation,
  useLazySearchHubspotContactsQuery,
  useSearchHubspotContactsQuery,
} from "../store/api";
import { selectEmail } from "../store/cashier";
import {
  APP_CHARGING,
  HUBSPOTRETRIES,
  PRIV_ADMIN,
  PRIV_CREATEDEAL,
} from "../store/constants";
import { Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MdSearch } from "react-icons/md";
import { useDispatch } from "react-redux";
import { twMerge } from "tailwind-merge";
import * as Yup from "yup";

import Backdrop from "./Backdrop";
import StyledFormikField from "./StyledFormikField";

export const ChosenEmail = (props) => (
  <div className="flex flex-col">
    <div className="my-1 mr-auto rounded border border-solid bg-gray-50 p-2 shadow-md">
      <i className="">{props.chosenEmail}</i>
      <span className="ml-2 mr-1 cursor-pointer" onClick={props.onClear}>
        X
      </span>
    </div>
    {props.children}
  </div>
);

// eslint-disable-next-line no-undef
const devMode = process.env.REACT_APP_devMode;

const CreateContactDialog = (props) => {
  const { email, close } = props;
  const formRef = useRef();

  const [createContactCall] = useCreateHubspotContactMutation();

  const lazyContactQuery = useLazySearchHubspotContactsQuery();
  const checkContactExists = async (email, counter = 0) => {
    const [searchContact] = lazyContactQuery;
    const results = await searchContact({ email }, false).unwrap();

    if (results.length) {
      return;
    }

    if (counter < HUBSPOTRETRIES) {
      setTimeout(() => {
        checkContactExists(email, counter + 1); // recursive
      }, 1000);
    } else {
      setProcessingText({
        text: "Never got an answer from hubspot, please CLICK HERE to refresh",
        url: `${location.href}?email=${email}`,
      });
    }
  };

  const createContact = async ({ email, fullName }) => {
    await createContactCall({ email, fullName });

    checkContactExists(email);
  };

  const handleKey = (event) => {
    switch (event?.key) {
      case "Enter":
        formRef.current.onSubmit();
        break;
      case "Escape":
        close();
        break;
      default:
        break;
    }
  };

  const [processingText, setProcessingText] = useState({ text: "", url: "" });

  useEffect(() => {
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("keydown", handleKey);
    };
  });

  const handleSubmit = async (props) => {
    setProcessingText({
      text: "Waiting for customer to show up in Hubspot…",
      url: null,
    });

    createContact({
      email,
      fullName: props.name,
    });
  };

  return createPortal(
    <>
      <Backdrop />
      <div className="fixed z-[2] flex size-full flex-col items-center justify-center">
        <div className="w-9/12 rounded bg-white p-2 shadow-lg lg:w-6/12 xl:w-4/12">
          <Formik
            innerRef={formRef}
            initialValues={{
              name: "",
            }}
            validationSchema={Yup.object({
              name: Yup.string().required().min(3),
            })}
            onSubmit={handleSubmit}
          >
            {(form) => {
              const { errors } = form;
              const anyError = !!Object.keys(errors).length;
              return (
                <Form>
                  <div className="p-2">
                    <div className="my-1 mt-4 text-blue-400">
                      Create Hubspot Contact
                    </div>
                    <div> Email: {email}</div>
                    <div className={twMerge("flex flex-col")}>
                      <label htmlFor="name" className="my-1 mt-4 text-blue-400">
                        Name
                      </label>
                      <StyledFormikField
                        id="name"
                        name="name"
                      ></StyledFormikField>
                    </div>
                    {processingText?.text && (
                      <span
                        className={twMerge(
                          "text-green-700",
                          processingText?.url && "cursor-pointer text-red-500",
                        )}
                      >
                        {processingText?.url ? (
                          <a href={processingText.url}>{processingText.text}</a>
                        ) : (
                          processingText.text
                        )}
                      </span>
                    )}
                    <div className="flex gap-x-4">
                      <button
                        disabled={processingText?.text}
                        className={twMerge(
                          "my-2 w-full rounded border p-2 font-semibold",
                        )}
                        onClick={close}
                      >
                        Cancel
                      </button>
                      <button
                        disabled={anyError || processingText?.text}
                        className={twMerge(
                          "my-2 w-full bg-blue-400 p-2 font-semibold text-white",
                          (anyError || processingText?.text) && "bg-gray-400",
                        )}
                        type="submit"
                      >
                        Create
                      </button>
                    </div>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </>,
    document.getElementById("modaldiv"),
  );
};

const CreateContact = (props) => {
  const { email, onCreate, looking } = props;
  const [dialog, setDialog] = useState(false);
  const { hasRole } = useClerkInfo();

  const isAdmin = hasRole(APP_CHARGING, PRIV_ADMIN);
  const canCreateContact = hasRole(APP_CHARGING, PRIV_CREATEDEAL); // TODO warning: createdeal priv for create contact too (?)

  // if no access, this is the end
  if (!isAdmin && !canCreateContact) {
    return `-No matches for "${email}"-`; // in hubspot
  }

  // Suggest creation
  return (
    <div>
      {!looking && (
        <div>
          No matches for {email}
          {isAdmin && (
            <button
              className={twMerge(
                "my-2 w-full bg-blue-400 p-2 font-semibold text-white",
              )}
              onClick={() => setDialog(true)}
              type="button"
            >
              Create contact
            </button>
          )}
        </div>
      )}
      {dialog && (
        <CreateContactDialog
          email={email}
          close={() => setDialog(() => false)}
          onCreate={onCreate}
        />
      )}
    </div>
  );
};

const EmailLookup = (props) => {
  const {
    onClear,
    currentEmail, // current form value if we're hidden and shown again
  } = props;
  // get deals with hook etc

  const dispatch = useDispatch();

  const [email, debounceEmail] = useDebouncedInput({
    defaultText: currentEmail,
  });
  const [searching, setSearching] = useState(false);

  const handleChange = (email) => {
    if (email.trim().length < 3) {
      debounceEmail(email, true);
      setUniqueEmails(() => []);
      return;
    }
    setSearching(() => true);
    debounceEmail(email);
  };

  const { data: hsContacts, isFetching: isFetchingContacts } =
    useSearchHubspotContactsQuery({ email }, { skip: email.trim().length < 3 });

  const setContact = (email) => {
    dispatch(selectEmail(email));
  };

  const [uniqueEmails, setUniqueEmails] = useState([]);

  useEffect(() => {
    setSearching(() => false);
    if (!hsContacts?.length) {
      return setUniqueEmails(() => []);
    }

    setUniqueEmails(() => hsContacts.map((entry) => entry.properties.email));

    // if we want to autoselect
    if (hsContacts?.length === 1) {
      onClear(); // must clear to avoid autoselecting by default next time
      const { email } = hsContacts[0].properties;
      setContact(email);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hsContacts]);

  return (
    <>
      <label htmlFor="email" className="mt-4 text-blue-400">
        by email{devMode && " (try arnotixe)"}
      </label>

      <div>
        <StyledFormikField
          type="email"
          className={twMerge("w-full p-2")}
          name="email"
          tellChange={handleChange}
          iconclassname={searching ? " animate-ping" : ""}
          icon={searching ? MdSearch : null}
        />

        <ul>
          {!uniqueEmails.length && email.trim() && (
            <i>
              {email.trim().length < 3
                ? "Enter at least 3 characters"
                : isFetchingContacts && "Loading emails…"}
              {/* don't remount createcontact on re-fetch */}

              <CreateContact
                looking={isFetchingContacts || searching}
                email={email}
                onCreate={setContact}
              />
            </i>
          )}

          {uniqueEmails.map((eml, index) => (
            <li
              onClick={() => setContact(eml)}
              className="mt-2 cursor-pointer"
              key={eml}
            >
              <i>{index + 1}</i>
              {": " + eml}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

export default EmailLookup;
